import { useEffect, useRef, useState } from 'react'
import * as d3 from 'd3'

interface TwitterNetworkGraphProps {
  userInfo: any
  followers: any[]
  followings: any[]
  onNodeClick?: (node: any) => void
  height?: number
}

const getNestedValue = (entity: any, path: string) => {
  if (!entity) return undefined
  const segments = path.split('.')
  let current: any = entity
  for (const segment of segments) {
    if (current == null) {
      return undefined
    }
    current = current[segment]
  }
  return current
}

const resolveMetricValue = (entity: any, fields: string[]) => {
  for (const field of fields) {
    const value = getNestedValue(entity, field)
    if (value !== undefined && value !== null) {
      if (typeof value === 'number') {
        return value
      }
      if (typeof value === 'string') {
        const parsed = parseInt(value, 10)
        if (!Number.isNaN(parsed)) {
          return parsed
        }
      }
    }
  }
  return 0
}

const getAvatarUrl = (entity: any) => {
  if (!entity) return ''
  const avatarKeys = [
    'profilePicture',
    'avatar',
    'profile_image_url',
    'profile_image_url_https',
    'profile_image_url_https_200x200',
    'profileImageUrl',
    'profile_image_url_orig'
  ]
  for (const key of avatarKeys) {
    const value = getNestedValue(entity, key)
    if (value) {
      return value
    }
  }
  return ''
}

const getUsernameValue = (entity: any) => {
  if (!entity) return ''
  return (
    entity.userName ||
    entity.username ||
    entity.screenName ||
    entity.handle ||
    entity.displayName ||
    ''
  )
}

const followerMetricFields = ['followers', 'followersCount', 'followers_count', 'public_metrics.followers_count']
const followingMetricFields = ['following', 'followingCount', 'following_count', 'public_metrics.following_count']

export function TwitterNetworkGraph({ 
  userInfo, 
  followers = [], 
  followings = [], 
  onNodeClick,
  height = 600 
}: TwitterNetworkGraphProps) {
  const svgRef = useRef<SVGSVGElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const simulationRef = useRef<d3.Simulation<d3.SimulationNodeDatum, undefined> | null>(null)
  const [selectedNode, setSelectedNode] = useState<any>(null)

  useEffect(() => {
    if (!svgRef.current || !userInfo) return

    // Clear previous
    d3.select(svgRef.current).selectAll("*").remove()

    // Build nodes and links
    const nodes: any[] = []
    const links: any[] = []

    // Add central user node
    const centralData = userInfo.data || {}
    const centralLabel = centralData.name || centralData.displayName || getUsernameValue(centralData) || 'User'
    const centralNode = {
      id: centralData.id || centralData.userId || 'central',
      label: centralLabel,
      username: getUsernameValue(centralData) || 'central',
      type: 'user',
      isCentral: true,
      followers: resolveMetricValue(centralData, followerMetricFields),
      following: resolveMetricValue(centralData, followingMetricFields),
      verified: centralData.isBlueVerified || centralData.verified || false,
      avatar: getAvatarUrl(centralData),
      description: centralData.description || centralData.bio || ''
    }
    nodes.push(centralNode)

    // Add follower nodes
    followers.forEach((follower, index) => {
      const nodeId = follower.id || follower.userId || `follower-${index}`
      nodes.push({
        id: nodeId,
        label: follower.name || follower.displayName || getUsernameValue(follower) || `Follower ${index + 1}`,
        username: getUsernameValue(follower),
        type: 'follower',
        followers: resolveMetricValue(follower, followerMetricFields),
        following: resolveMetricValue(follower, followingMetricFields),
        verified: follower.isBlueVerified || follower.verified || false,
        avatar: getAvatarUrl(follower),
        description: follower.description || follower.bio || ''
      })
      links.push({
        source: nodeId,
        target: centralNode.id,
        type: 'follows'
      })
    })

    // Add following nodes
    followings.forEach((following, index) => {
      const nodeId = following.id || following.userId || `following-${index}`
      const followingUsername = getUsernameValue(following)
      
      // Check if node already exists (mutual follow) - check by both ID and username
      const existingNode = nodes.find(
        (n) => {
          if (n.id === nodeId) return true
          // Check by username if both have usernames and it's not the central user
          if (n.username && followingUsername && n.username === followingUsername && n.type !== 'user') {
            return true
          }
          return false
        }
      )
      
      let targetNodeId: string
      if (!existingNode) {
        // Create new node
        const newNode = {
          id: nodeId,
          label: following.name || following.displayName || followingUsername || `Following ${index + 1}`,
          username: followingUsername,
          type: 'following',
          followers: resolveMetricValue(following, followerMetricFields),
          following: resolveMetricValue(following, followingMetricFields),
          verified: following.isBlueVerified || following.verified || false,
          avatar: getAvatarUrl(following),
          description: following.description || following.bio || ''
        }
        nodes.push(newNode)
        targetNodeId = newNode.id
      } else {
        // Mark as mutual and use existing node ID
        existingNode.type = 'mutual'
        targetNodeId = existingNode.id
      }
      
      // Only add link if both source and target nodes exist
      if (nodes.some(n => n.id === centralNode.id) && nodes.some(n => n.id === targetNodeId)) {
        links.push({
          source: centralNode.id,
          target: targetNodeId,
          type: 'follows'
        })
      } else {
        console.warn('Skipping link creation - node not found:', {
          source: centralNode.id,
          target: targetNodeId,
          sourceExists: nodes.some(n => n.id === centralNode.id),
          targetExists: nodes.some(n => n.id === targetNodeId)
        })
      }
    })

    if (nodes.length === 0) return

    // Validate links - ensure all source and target nodes exist
    const nodeIds = new Set(nodes.map(n => String(n.id)))
    const validLinks = links.filter(link => {
      const sourceId = typeof link.source === 'object' ? (link.source as any).id : link.source
      const targetId = typeof link.target === 'object' ? (link.target as any).id : link.target
      
      // Convert to string for comparison
      const sourceIdStr = String(sourceId)
      const targetIdStr = String(targetId)
      
      const sourceExists = nodeIds.has(sourceIdStr)
      const targetExists = nodeIds.has(targetIdStr)
      
      if (!sourceExists || !targetExists) {
        console.warn('Invalid link detected and filtered out:', { 
          source: sourceIdStr, 
          target: targetIdStr, 
          sourceExists, 
          targetExists,
          availableNodeIds: Array.from(nodeIds).slice(0, 10) // Show first 10 for debugging
        })
        return false
      }
      return true
    })
    
    // Additional safety check - ensure all link IDs are strings and match node IDs
    const finalValidLinks = validLinks.map(link => {
      const sourceId = typeof link.source === 'object' ? (link.source as any).id : String(link.source)
      const targetId = typeof link.target === 'object' ? (link.target as any).id : String(link.target)
      return {
        ...link,
        source: sourceId,
        target: targetId
      }
    }).filter(link => {
      return nodeIds.has(String(link.source)) && nodeIds.has(String(link.target))
    })

    const width = containerRef.current?.clientWidth || 800
    const containerHeight = height

    const svg = d3.select(svgRef.current)
      .attr("width", width)
      .attr("height", containerHeight)
      .style("background", "#0a0a0a")

    // Create zoom behavior
    const zoom = d3.zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.1, 4])
      .on("zoom", (event) => {
        g.attr("transform", event.transform.toString())
      })

    svg.call(zoom as any)

    const g = svg.append("g")

    // Create force simulation - use finalValidLinks instead of validLinks
    const simulation = d3.forceSimulation(nodes as d3.SimulationNodeDatum[])
      .force("link", d3.forceLink(finalValidLinks).id((d: any) => String(d.id)).distance(80))
      .force("charge", d3.forceManyBody().strength(-400))
      .force("center", d3.forceCenter(width / 2, containerHeight / 2))
      .force("collision", d3.forceCollide().radius((d: any) => {
        if (d.isCentral) return 25
        return 15
      }))

    // Create links - use finalValidLinks
    const link = g.append("g")
      .selectAll("line")
      .data(finalValidLinks)
      .enter()
      .append("line")
      .attr("stroke", (d: any) => {
        if (d.type === 'follows') return "#00ff00"
        return "#666666"
      })
      .attr("stroke-opacity", 0.3)
      .attr("stroke-width", 1.5)

    // Create nodes
    const node = g.append("g")
      .selectAll("g")
      .data(nodes)
      .enter()
      .append("g")
      .attr("class", "node")
      .call(d3.drag<SVGGElement, any>()
        .on("start", (event, d: any) => {
          if (!event.active) simulation.alphaTarget(0.3).restart()
          d.fx = d.x
          d.fy = d.y
        })
        .on("drag", (event, d: any) => {
          d.fx = event.x
          d.fy = event.y
        })
        .on("end", (event, d: any) => {
          if (!event.active) simulation.alphaTarget(0)
          d.fx = null
          d.fy = null
        }) as any
      )
      .on("click", (_event, d: any) => {
        setSelectedNode(d)
        if (onNodeClick) onNodeClick(d)
      })

    // Add circles for nodes
    node.append("circle")
      .attr("r", (d: any) => d.isCentral ? 25 : 15)
      .attr("fill", (d: any) => {
        if (d.isCentral) return "#00ff00"
        if (d.type === 'mutual') return "#ffaa00"
        if (d.type === 'follower') return "#00aaff"
        return "#aa00ff"
      })
      .attr("stroke", "#ffffff")
      .attr("stroke-width", 2)
      .style("cursor", "pointer")

    // Add images for avatars
    node.append("image")
      .attr("xlink:href", (d: any) => d.avatar || "")
      .attr("x", (d: any) => d.isCentral ? -20 : -12)
      .attr("y", (d: any) => d.isCentral ? -20 : -12)
      .attr("width", (d: any) => d.isCentral ? 40 : 24)
      .attr("height", (d: any) => d.isCentral ? 40 : 24)
      .attr("clip-path", (d: any) => `circle(${d.isCentral ? 20 : 12}px)`)
      .style("opacity", (d: any) => d.avatar ? 1 : 0)
      .on("error", function() {
        d3.select(this).style("opacity", 0)
      })

    // Add labels
    node.append("text")
      .text((d: any) => d.username || d.label)
      .attr("dx", (d: any) => d.isCentral ? 0 : 0)
      .attr("dy", (d: any) => d.isCentral ? 35 : 25)
      .attr("text-anchor", "middle")
      .attr("fill", "#ffffff")
      .attr("font-size", (d: any) => d.isCentral ? "12px" : "10px")
      .attr("font-weight", (d: any) => d.isCentral ? "bold" : "normal")
      .style("pointer-events", "none")

    // Update positions on tick
    simulation.on("tick", () => {
      link
        .attr("x1", (d: any) => d.source.x)
        .attr("y1", (d: any) => d.source.y)
        .attr("x2", (d: any) => d.target.x)
        .attr("y2", (d: any) => d.target.y)

      node.attr("transform", (d: any) => `translate(${d.x},${d.y})`)
    })

    simulationRef.current = simulation

    // Cleanup
    return () => {
      simulation.stop()
    }
  }, [userInfo, followers, followings, height, onNodeClick])

  return (
    <div ref={containerRef} className="w-full">
      {selectedNode && (
        <div className="mb-4 p-3 bg-gray-800 rounded-lg border border-green-500">
          <h4 className="font-bold text-white">{selectedNode.label}</h4>
          <p className="text-sm text-gray-400">@{selectedNode.username}</p>
          <div className="flex gap-4 mt-2 text-xs text-gray-400">
            <span>{selectedNode.followers?.toLocaleString() || 0} followers</span>
            <span>{selectedNode.following?.toLocaleString() || 0} following</span>
          </div>
        </div>
      )}
      <svg ref={svgRef} className="w-full border rounded-lg border-green-500" />
    </div>
  )
}

